import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useMallSearch from "@saleor/searches/useMallSearch";
import useSalesSearch from "@saleor/searches/useSaleSearch";
import useVoucherSearch from "@saleor/searches/useVoucherSearch";
import { SmopBannerTypeEnum } from "@saleor/types/globalTypes";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import React from "react";
import { useIntl } from "react-intl";

import BannerCreatePage from "../components/BannerCreatePage";
import { BannerDetailsPageTab } from "../components/BannerDetailsPage";
import { TypedBannerCreate } from "../mutations";
import { BannerCreate } from "../types/BannerCreate";
import {
  bannerCreateTabUrl,
  bannerListUrl,
  bannerUrl,
  BannerUrlQueryParams
} from "../urls";

export const SaleDetails: React.FC = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const changeTab = (tab: BannerDetailsPageTab) => {
    const queryParams: BannerUrlQueryParams = { activeTab: tab };
    navigate(bannerCreateTabUrl(queryParams), false, false, false);
  };

  const { search: searchSales, result: sales } = useSalesSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const { search: searchVouchers, result: vouchers } = useVoucherSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const {
    loadMore: loadMoreMalls,
    search: searchMalls,
    result: malls
  } = useMallSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const handleBannerCreate = (data: BannerCreate) => {
    if (data.bannerCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully created banner"
        })
      });
      navigate(bannerUrl(data.bannerCreate.banner.id), true);
    }
  };

  const getCurrentTab = () => {
    const url = new URL(window.location.href);
    const searchString = url.searchParams.get("activeTab");
    return searchString;
  };
  const currentTab = getCurrentTab();

  return (
    <TypedBannerCreate onCompleted={handleBannerCreate}>
      {(BannerCreate, BannerCreateOpts) => (
        <>
          <WindowTitle title={intl.formatMessage(sectionNames.banners)} />
          <BannerCreatePage
            disabled={BannerCreateOpts.loading}
            errors={BannerCreateOpts.data?.bannerCreate.errors || []}
            onBack={() => navigate(bannerListUrl())}
            fetchMalls={searchMalls}
            fetchSales={searchSales}
            fetchVouchers={searchVouchers}
            fetchMoreMalls={{
              hasMore: maybe(() => malls.data.search.pageInfo.hasNextPage),
              loading: malls.loading,
              onFetchMore: loadMoreMalls
            }}
            malls={maybe(
              () => malls.data.search.edges.map(edge => edge.node),
              []
            )}
            onSubmit={formData => {
              const additionalParams = {
                descriptionJson: JSON.stringify(formData.description),
                endDate: changeTimeZone(formData.endDate, formData.endTime),
                mall: formData.mall === "all" ? null : formData.mall,
                name: formData.name,
                sale: null,
                startDate: changeTimeZone(
                  formData.startDate,
                  formData.startTime
                ),
                verticals: formData.verticals || [],
                voucher: null
              };
              if (formData.type === SmopBannerTypeEnum.SALE) {
                additionalParams.sale = formData.saleId;
                additionalParams.name = formData.saleName;
                additionalParams.mall = null;
                additionalParams.startDate = null;
                additionalParams.endDate = null;
                additionalParams.descriptionJson = null;
              } else if (formData.type === SmopBannerTypeEnum.VOUCHER) {
                additionalParams.voucher = formData.voucherId;
                additionalParams.name = formData.voucherName;
                additionalParams.mall = null;
                additionalParams.startDate = null;
                additionalParams.endDate = null;
                additionalParams.descriptionJson = null;
              }
              BannerCreate({
                variables: {
                  input: {
                    ...additionalParams,
                    imageUrl: null,
                    status: formData.status,
                    type: formData.type
                  }
                }
              });
            }}
            sales={maybe(
              () => sales.data.search.edges.map(edge => edge.node),
              []
            )}
            saveButtonBarState={BannerCreateOpts.status}
            vouchers={maybe(
              () => vouchers.data.search.edges.map(edge => edge.node),
              []
            )}
            onTabClick={changeTab}
            activeTab={
              (currentTab as BannerDetailsPageTab) ||
              BannerDetailsPageTab.products
            }
          />
        </>
      )}
    </TypedBannerCreate>
  );
};
export default SaleDetails;
